<template>
  <div class="d-flex flex-column gap-4 flex-grow-1 flex-shrink-1">
    <loading-overlay v-if="isLoading" :show="true" message="Processing verification..." />

    <div v-if="verificationError" class="id-verification__error">
      <v-alert type="warning" icon="mdi-alert-circle-outline" prominent>
        <div class="d-flex flex-column gap-2">
          <span class="typography-body-md-bold">ID Verification Issue</span>
          <span class="typography-body-sm">{{ verificationError }}</span>
        </div>
      </v-alert>
    </div>

    <div class="checkin__form-container d-flex flex-column gap-4 flex-grow-1 flex-shrink-1">
      <div class="id-verification__persona-container flex-grow-1 flex-shrink-1">
        <Inquiry
          v-if="templateId && personaEnvironmentId"
          :template-id="templateId"
          :environment-id="personaEnvironmentId"
          @complete="handleComplete"
          @error="handleError"
          @cancel="handleCancel" />
        <v-alert v-else type="error" icon="mdi-alert-circle-outline">
          ID verification is not properly configured for this warehouse.
        </v-alert>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import type { PropType } from 'vue';
import { Inquiry } from 'persona-vue';
import type { IWarehouse } from '@nova/core';
import { useAxiosClient } from '@/components/composables';
import { personaEnvironmentId } from '@/config';
import LoadingOverlay from './LoadingOverlay.vue';

interface PersonaCompleteEvent {
  inquiryId: string;
  status: string;
  fields: Record<string, unknown>;
}

interface PersonaErrorEvent {
  status: string;
  code?: string;
}

export default defineComponent({
  name: 'IdVerification',
  components: {
    Inquiry,
    LoadingOverlay
  },
  props: {
    warehouse: {
      type: Object as PropType<IWarehouse>,
      required: true
    }
  },
  emits: ['complete', 'error', 'skip'],
  setup(props, { emit }) {
    const axiosClient = useAxiosClient();
    const isLoading = ref(false);
    const verificationError = ref<string | null>(null);
    const hasCompletedVerification = ref(false);

    const templateId = computed(() => {
      return props.warehouse.settings?.checkinIdValidationTemplateId as string | undefined;
    });

    const isValidationRequired = computed(() => {
      return props.warehouse.settings?.checkinIdValidationRequired === true;
    });

    const isEnabled = computed(() => {
      return props.warehouse.settings?.enableCheckinIdValidation === true;
    });

    const handleComplete = async (event: PersonaCompleteEvent) => {
      // TODO(william): This is a temporary fix to prevent multiple calls to the complete-id-verification/checkin endpoints.
      // It should be removed once we find why the @complete event is being called twice.
      if (hasCompletedVerification.value) {
        return;
      }

      const { inquiryId, status } = event;
      isLoading.value = true;
      verificationError.value = null;
      hasCompletedVerification.value = true;

      try {
        await axiosClient.post(`/checkin/public/${props.warehouse.id}/complete-id-verification`, {
          inquiryId
        });

        if (['completed', 'approved'].includes(status)) {
          emit('complete', { inquiryId, status, success: true });
        } else {
          if (isValidationRequired.value) {
            verificationError.value =
              'ID verification was not successful. Please try again or contact the warehouse.';
            emit('error', { inquiryId, status, message: verificationError.value });
          } else {
            verificationError.value =
              'ID verification could not be completed, but you may proceed with check-in.';
            emit('complete', { inquiryId, status, success: false });
          }
        }
      } catch (error: unknown) {
        hasCompletedVerification.value = false;

        console.error('Failed to complete ID verification:', error);

        if (isValidationRequired.value) {
          verificationError.value = 'Failed to process ID verification. Please try again.';
          emit('error', { inquiryId, status: 'failed', message: verificationError.value });
        } else {
          verificationError.value =
            'ID verification processing failed, but you may proceed with check-in.';
          emit('complete', { inquiryId, status: 'failed', success: false });
        }
      } finally {
        isLoading.value = false;
      }
    };

    const handleError = (event: PersonaErrorEvent) => {
      console.error('Persona verification error:', event);

      if (isValidationRequired.value) {
        verificationError.value = 'An error occurred during ID verification. Please try again.';
        emit('error', { status: event.status, code: event.code });
      } else {
        verificationError.value =
          'ID verification encountered an issue, but you may proceed with check-in.';
        emit('skip', { reason: 'error', event });
      }
    };

    const handleCancel = () => {
      if (isValidationRequired.value) {
        verificationError.value =
          'ID verification is required to complete check-in. Please try again.';
      } else {
        emit('skip', { reason: 'cancelled' });
      }
    };

    return {
      isLoading,
      verificationError,
      templateId,
      isEnabled,
      isValidationRequired,
      personaEnvironmentId,
      handleComplete,
      handleError,
      handleCancel
    };
  }
});
</script>

<style lang="scss" scoped>
.id-verification {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.id-verification__error {
  margin-bottom: 16px;
}

.id-verification__persona-container > iframe {
  min-height: 100%;
  width: 100%;
}
</style>
